import { Button, DatePicker, Space, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { DateFilterProps, DateFilterValue } from './types';

import s from './DateFilter.module.scss';

const DateFilter: React.FC<DateFilterProps> = ({
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  close,
}) => {
  const { t } = useTranslation();
  const currentValue: DateFilterValue = React.useMemo(() => {
    if (selectedKeys.length > 0 && typeof selectedKeys[0] === 'string') {
      try {
        return JSON.parse(selectedKeys[0]) as DateFilterValue;
      } catch {
        return {};
      }
    }
    return {};
  }, [selectedKeys]);

  const [from, setFrom] = React.useState<Dayjs | null>(
    currentValue.from ? dayjs(currentValue.from) : null
  );
  const [to, setTo] = React.useState<Dayjs | null>(
    currentValue.to ? dayjs(currentValue.to) : null
  );

  React.useEffect(() => {
    setFrom(currentValue.from ? dayjs(currentValue.from) : null);
    setTo(currentValue.to ? dayjs(currentValue.to) : null);
  }, [currentValue.from, currentValue.to]);

  const handleApply = React.useCallback(() => {
    const filterValue: DateFilterValue = {};
    
    if (from) {
      filterValue.from = from.startOf('day').toISOString();
    }
    if (to) {
      filterValue.to = to.endOf('day').toISOString();
    }

    if (Object.keys(filterValue).length > 0) {
      setSelectedKeys([JSON.stringify(filterValue)]);
    } else {
      setSelectedKeys([]);
    }
    
    confirm();
  }, [from, to, setSelectedKeys, confirm]);

  const handleReset = React.useCallback(() => {
    setFrom(null);
    setTo(null);
    clearFilters?.();
  }, [clearFilters]);

  return (
    <div className={s.dateFilter}>
      <Space direction="vertical" size="small" className={s.dateFilter__content}>
        <div className={s.dateFilter__row}>
          <Typography.Text className={s.dateFilter__label}>{t('filter.from')}</Typography.Text>
          <DatePicker
            value={from}
            onChange={setFrom}
            placeholder={t('filter.start') as string}
            className={s.dateFilter__picker}
            format="DD.MM.YYYY"
          />
        </div>
        
        <div className={s.dateFilter__row}>
          <Typography.Text className={s.dateFilter__label}>{t('filter.to')}</Typography.Text>
          <DatePicker
            value={to}
            onChange={setTo}
            placeholder={t('filter.end') as string}
            className={s.dateFilter__picker}
            format="DD.MM.YYYY"
          />
        </div>
      </Space>

      <Space className={s.dateFilter__buttons}>
        <Button size="small" onClick={handleReset}>
          {t('filter.reset')}
        </Button>
        <Button type="primary" size="small" onClick={handleApply}>
          {t('filter.apply')}
        </Button>
      </Space>
    </div>
  );
};

export default DateFilter;
